<script setup lang="ts">
import { Field } from 'vee-validate'
import { computed } from 'vue'
import { toTypedSchema } from '@vee-validate/zod'
import { z } from 'zod'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useStore } from '@/stores/store'
import BigSizePopup from '@/components/SizeAndButtons/BigSizePopup.vue'
import { IconInfo } from '@/components/icons'

const store = useStore()
const { t } = useI18n()
const { diameter_a_circle } = storeToRefs(store)

const CIRCLE_MIN_VALUE = 30
const CIRCLE_MAX_VALUE = 200
const CIRCLE_BIG_SIZE = 160

const isBigSize = computed(() => Number(diameter_a_circle.value) >= CIRCLE_BIG_SIZE)

const diameterFieldRule = toTypedSchema(
  z.coerce
    .number({
      required_error: `${t('Diameter')} ${t('must be filled in.')}`,
      invalid_type_error: `${t('Diameter')} ${t('must be a number.')}`,
    })
    .min(CIRCLE_MIN_VALUE, {
      message: `${t('Diameter')} ${t('must be greater than or equal to')} ${CIRCLE_MIN_VALUE}`,
    })
    .max(CIRCLE_MAX_VALUE, {
      message: `${t('Diameter')} ${t('must be less than or equal to')} ${CIRCLE_MAX_VALUE}`,
    }),
)
</script>
<template>
  <div class="fields-wrapper space-y-2">
    <div class="form-group half-row">
      <label class="items-center" for="diameter_a_circle">
        <span class="field-label">{{ $t('Diameter') }}</span>
        <span class="field-condition">{{ $t('Minimal') + ' ' + CIRCLE_MIN_VALUE }}</span>
      </label>
      <Field
        keepValue
        id="diameter_a_circle"
        validateOnInput
        name="diameter_a_circle"
        type="number"
        v-model="diameter_a_circle"
        :rules="diameterFieldRule"
        v-slot="{ field, errorMessage }"
      >
        <input
          v-bind="field"
          class="!field-input"
          placeholder="cm"
          type="number"
          inputmode="numeric"
          :min="CIRCLE_MIN_VALUE"
          :max="CIRCLE_MAX_VALUE"
          :class="{ 'has-error': errorMessage }"
        />

        <span v-if="errorMessage">{{ errorMessage }}</span>
      </Field>
    </div>
    <div
      v-if="isBigSize"
      class="contact-form-link flex flex-col items-start bg-black/80 px-6 py-4 text-white w-full max-w-[480px] space-y-4"
    >
      <div class="flex gap-2 justify-center items-center">
        <IconInfo class="text-white w-6 h-6" />
        <span class="w-full">
          {{
            $t(
              "To make a cushion of the measurements you've entered, we need to assemble two or more pieces of material.",
            )
          }}
        </span>
      </div>
      <BigSizePopup v-if="isBigSize" />
    </div>
  </div>
</template>

<style scoped>
.form-group {
  position: relative;
}
.field-label {
  font-size: 24px;
  font-weight: 600;
  margin-right: 16px;
}
.field-condition {
  color: hsla(0 0% 0% / 0.6);
  font-size: 16px;
}
</style>
